/**
 * Routes Laravel validation errors to the FormModal tab that can show them. Each tabbed form
 * (events, event series, job roles) maps a tab to the field names it owns in its own file;
 * only this generic error-routing logic is shared.
 */
export interface ITabDef {
  /** Stable id used to hide a tab conditionally without the visible-tab filtering
   *  depending on array position. */
  key: string
  label: string
  fields: string[]
}

/** Laravel returns dotted paths ("tickets.0.title"); only the root maps to a tab. */
const rootKey = (key: string) => key.split('.')[0]

const hasMessage = (value: unknown) => (Array.isArray(value) ? value.length > 0 : Boolean(value))

/** Number of error messages sitting in each tab, in the given tab order. */
export function countErrorsPerTab(tabs: ITabDef[], errors: Record<string, unknown>): number[] {
  const counts = tabs.map(() => 0)

  Object.entries(errors).forEach(([key, value]) => {
    if (!hasMessage(value)) return
    const tabIndex = tabs.findIndex((tab) => tab.fields.includes(rootKey(key)))
    if (tabIndex >= 0) counts[tabIndex] += 1
  })

  return counts
}

/** Index of the first tab holding an error, or -1 when there is none. */
export function firstTabWithErrors(tabs: ITabDef[], errors: Record<string, unknown>): number {
  return countErrorsPerTab(tabs, errors).findIndex((count) => count > 0)
}

/**
 * Every message from `errors` whose root key doesn't belong to any tab in `tabs` — e.g. a
 * bare `error`/`message` key, or a field a tab definition hasn't caught up with yet. Used
 * to surface something concrete when `firstTabWithErrors` returns -1, instead of jumping
 * to a tab that has nothing to show for it.
 */
export function unmappedErrorMessages(tabs: ITabDef[], errors: Record<string, unknown>): string[] {
  const messages: string[] = []

  Object.entries(errors).forEach(([key, value]) => {
    if (!hasMessage(value)) return
    if (tabs.some((tab) => tab.fields.includes(rootKey(key)))) return
    if (Array.isArray(value)) messages.push(...value.map(String))
    else messages.push(String(value))
  })

  return messages
}

/** True for `field` itself and for its items (`duties.3`, `emergency_contacts.0.name`). */
const isFieldOrItem = (key: string, field: string) => key === field || key.startsWith(`${field}.`)

/** `errors` without the ones of the given fields and of their items: used once those fields are edited. */
export function withoutFieldErrors<T>(errors: Record<string, T>, fields: string[]): Record<string, T> {
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) => !fields.some((field) => isFieldOrItem(key, field)))
  )
}

/** The field's own messages plus its items' (`duties.3`), for a field that shows them together. */
export function errorsOfField(errors: Record<string, string[]>, field: string): string[] {
  return Object.entries(errors)
    .filter(([key]) => isFieldOrItem(key, field))
    .flatMap(([, messages]) => messages)
}
